import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Input,
  Text,
  HStack,
  ModalHeader,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Textarea,
  Icon,
  useDisclosure,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { fetchProcessVaraiblesbyProcessId } from "../../services/state/Process-modular/processModular-async-calls";
import { Option } from "../Select/DropDown";
import { fetchRules } from "../../services/state/rules/rules-async-calls";
import { fetchNodeIODetailsByNodeUuid } from "../../services/state/node-io/nodeInputsOutputs-async-calls";
import { BrandButton } from "../BrandButton/BrandButton";
import { useMonaco } from "@monaco-editor/react";
import { v4 } from "uuid";
import { Node } from "@xyflow/react";
import { ExpressionEditorModal } from "../ExpressionEditorModal/ExpressionEditorModal";
import { ServiceProperties } from "./SerivceProperties";
import { fetchHttpIntegrationsByApplication } from "../../services/state/http-integration/httpIntegration-async-calls";
import { HttpIntegration } from "@/services/state/http-integration/httpIntegration-interface";
import { GatewayProperties } from "./GatewayProperties";
import { DataProperties } from "./DataProperties";
import { BiEdit } from "react-icons/bi";
import { EmailProperties } from "./EmailProperties";
import { SubProcessProperties } from "./SubProcessProperties";
import { Process } from "@/services/state/processes/processes-interface";
import { fetchDataTypes } from "../../services/state/custom-data-types/customDataTypes-async-calls";
import { fetchAllProcesses } from "../../services/state/processes/processes-async-calls";

export interface NodeInputOutput {
  name: string;
  uuid: string;
  expression: string;
  ioType: "I" | "O";
  target:
    | {
        id: string;
        name: string;
        component: JSX.Element;
      }
    | undefined;
  isDefault: boolean;
}

export const NodePropertiesModal = ({
  isOpen,
  onClose,
  edit = false,
  nodeId,
  handleNodeIoDetails,
}: {
  isOpen: boolean;
  onClose: () => void;
  edit?: boolean;
  nodeId?: string;
  handleNodeIoDetails: (
    id: string | undefined,
    nodeInfo: {
      name: string;
      description: string;
      displayName: string;
    },
    ioDetails: any,
    gatewayDetails:
      | {
          expression: string;
          nextNodeUuid: string;
        }[]
      | undefined,
    nodeExecDetails:
      | {
          integrationUuid: string;
          inputs: string[];
          targetVariableUuid: string;
        }
      | undefined,
    subProcessDetails:
      | {
          subProcess?: string;
          expression?: string | null;
          isDynamic?: boolean;
          runAs: string;
          subProcessInputs: string | null;
          subProcessVariables: {
            inputVar: string | null;
            expression: string;
            isDynamic: boolean;
          }[];
          subProcessOutputs: string[];
        }
      | undefined
  ) => void;
}) => {
  const initialRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const monaco = useMonaco();

  const {
    isOpen: isExpressionEditorModalOpen,
    onOpen: onExpressionEditorModalOpen,
    onClose: onExpressionEditorModalClose,
  } = useDisclosure();
  const [nodeName, setNodeName] = useState<string | undefined>();
  const [nodeDescription, setNodeDescription] = useState<string>();
  const [nodeDisplayName, setNodeDisplayName] = useState<string>();
  const [nodeInputOutput, setNodeInputOutput] = useState<NodeInputOutput[]>([]);
  const [selectedInput, setSelectedInput] = useState<NodeInputOutput>();
  const [selectedOutput, setSelectedOutput] = useState<NodeInputOutput>();

  const [selectedHttpIntegration, setSelectedHttpIntegration] =
    useState<HttpIntegration>();
  const [selectedHttpIntegrationInputs, setSelectedHttpIntegrationInputs] =
    useState<string[]>([]);
  const [
    selectedHttpIntegrationTargetVariableUuid,
    setSelectedHttpIntegrationTargetVariableUuid,
  ] = useState<string>("");
  const [gateWayDetails, setGatewayDetails] = useState<
    { expression: string; result: string; resultName: string }[]
  >([]);
  const [selectedSubProcess, setSelectedSubProcess] = useState<Process | null>(
    null
  );
  const [selectedSubProcessVariables, setSelectedSubProcessVariables] =
    useState<
      {
        inputVar: string | null;
        expression: string;
        isDynamic: boolean;
      }[]
    >([]);
  const [selectedSubProcessOutputs, setSelectedSubProcessOutputs] = useState<
    string[]
  >([]);
  const [selectedSubProcessOutputsNames, setSelectedSubProcessOutputsNames] =
    useState<string[]>([]);
  const [subProcessRunAs, setSubProcessRunAs] = useState<string>("SYNC");
  const [selectedSubProcessIsExpression, setSelectedSubProcessIsExpression] =
    useState<boolean>(false);
  const [selectedSubProcessExpression, setSelectedSubProcessExpression] =
    useState<string | null>(null);
  const [
    selectedSubProcessInputExpression,
    setSelectedSubProcessInputExpression,
  ] = useState<string | null>(null);

  const nodes = useSelector((state: RootState) => state.processModeler.nodes);

  const edges = useSelector((state: RootState) => state.processModeler.edges);

  const rules = useSelector((state: RootState) => state.rules.rules);

  const process = useSelector((state: RootState) => state.processes.processes);

  const httpIntegrations = useSelector(
    (state: RootState) => state.httpIntegrations.httpIntegrations
  );

  const selectedNode = nodes.find((node) => node.data.id === nodeId);

  const selectedProcess = useSelector(
    (state: RootState) => state.processes.selectedProcess
  );

  const processVariables = useSelector(
    (state: RootState) => state.processModeler.processVariables
  );

  const dataTypes = useSelector(
    (state: RootState) => state.customDataTypes.dataTypes
  );
  const processVariableOptions = processVariables.map((pv) => ({
    id: pv.uuid,
    name: pv.varNm,
    component: <Text>{pv.varNm}</Text>,
  }));

  const handleValue = (value: string) => {
    setNodeDisplayName(value);
  };

  const ruleMatchers = rules.map(
    (rule) =>
      `rule!${rule.ruleNm.replace(/ /g, "_")}(${rule.ruleInputs
        .map((input) => input.paramNm)
        .join(", ")})`
  );
  const pvMatchers = processVariables.map(
    (pv) => `pv!${pv.varNm.replace(/ /g, "_")}`
  );
  const processModelMatchers = process.map(
    (proc) => `pm!${proc.processNm.replace(/ /g, "_")}`
  );

  const ruleMatchers1 = rules.map((rule) => ({
    name: `rule!${rule.ruleNm.replace(/ /g, "_")}`,
    value: `rule!${rule.uuid}`,
  }));

  const pvMatchers1 = processVariables.map((pv) => ({
    name: `pv!${pv.varNm.replace(/ /g, "_")}`,
    value: `pv!${pv.uuid}`,
  }));

  const pmMatchers1 = process.map((proc) => ({
    name: `pm!${proc.processNm.replace(/ /g, "_")}`,
    value: `pm!${proc.uuid}`,
  }));

  const reverseExpressionHandler = (expression: string) => {
    const matchers = [...ruleMatchers1, ...pvMatchers1, ...pmMatchers1];

    matchers.sort((a, b) => b.value.length - a.value.length);

    matchers.forEach((match) => {
      const regex = new RegExp(
        match.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g"
      );
      expression = expression.replace(regex, match.name);
    });

    return expression;
  };

  useEffect(() => {
    dispatch(fetchProcessVaraiblesbyProcessId(selectedProcess?.uuid));
    dispatch(fetchRules(selectedProcess?.app.uuid as string));
    dispatch(
      fetchHttpIntegrationsByApplication(selectedProcess?.app.uuid as string)
    );
    dispatch(fetchNodeIODetailsByNodeUuid(nodeId as string));
    dispatch(fetchDataTypes());
    dispatch(fetchAllProcesses());
  }, [isOpen]);

  useEffect(() => {
    if (selectedNode) {
      setNodeName(String(selectedNode?.data.label || ""));
      setNodeDescription(String(selectedNode?.data.description || ""));
      setNodeDisplayName(String(selectedNode?.data.displayName || ""));
    }
  }, [selectedNode]);

  useEffect(() => {
    if (selectedNode && nodes) {
      const ne = edges
        .filter((edge) => edge.source === selectedNode?.id)
        .map((edge) => nodes.find((node) => node.id === edge.target))
        .filter((node) => node !== undefined) as Node[];

      if (selectedNode.data.gatewayDetails) {
        const existingGatewayDetails = (
          selectedNode.data.gatewayDetails as {
            expression: string;
            nextNodeUuid: string;
          }[]
        ).map((it) => ({
          expression: it.expression,
          result: it.nextNodeUuid,
          resultName: nodes.find((node) => node.id === it.nextNodeUuid)?.data
            .label as string,
        }));

        const existingNodeEdges = existingGatewayDetails.map((gw) => gw.result);

        if (ne.length !== existingNodeEdges.length) {
          setGatewayDetails([
            ...existingGatewayDetails,
            ...ne
              .filter((edge) => !existingNodeEdges.includes(edge.id))
              .map((edge) => ({
                expression: "",
                result: edge.id,
                resultName: edge.data.label as string,
              })),
          ]);
        } else {
          setGatewayDetails(existingGatewayDetails);
        }
      } else {
        if (gateWayDetails.length === 0) {
          const initialGateWayDetails = ne.map((node) => ({
            expression: "",
            result: node.id,
            resultName: node.data.label as string,
          }));

          setGatewayDetails(initialGateWayDetails);
        }
      }
    }
  }, [selectedNode, nodes, edges]);

  useEffect(() => {
    if (selectedNode && httpIntegrations) {
      const integration = httpIntegrations.find(
        (intg) =>
          intg.uuid ===
          (selectedNode?.data.nodeExecDetails as { integrationUuid: string })
            ?.integrationUuid
      );
      setSelectedHttpIntegration(integration || httpIntegrations.at(0));

      // To avoid selectedIntgInouts becoming undefined
      const inputs = (
        selectedNode?.data.nodeExecDetails as {
          integrationUuid: string;
          inputs: string[];
        }
      )?.inputs;
      setSelectedHttpIntegrationInputs(inputs ? inputs : []);
    }
  }, [httpIntegrations, selectedNode]);

  const resultOutputs: NodeInputOutput[] = [
    {
      name: "Result",
      uuid: v4(),
      expression: "exp!(return nv!result)",
      ioType: "O",
      target: undefined,
      isDefault: true,
    },
    {
      name: "Result.body",
      uuid: v4(),
      expression: "exp!(return nv!result.body)",
      ioType: "O",
      target: undefined,
      isDefault: true,
    },
    {
      name: "Result.headers",
      uuid: v4(),
      expression: "exp!(return nv!result.headers)",
      ioType: "O",
      target: undefined,
      isDefault: true,
    },
  ];

  useEffect(() => {
    const node = nodes.find((node) => node.data.id === nodeId);

    if (node) {
      const details = node.data.nodeIoDetails as {
        uuid: string;
        expression: string;
        ioType: "I" | "O";
        target: string;
        isDefault: boolean;
      }[];

      const mappedDetails: NodeInputOutput[] = details?.map((detail) => {
        const pv = processVariables.find((pv) => pv.uuid === detail.target);

        return {
          name: detail.expression,
          expression: detail.expression,
          ioType: detail.ioType,
          target: {
            id: pv.uuid,
            name: pv.varNm,
            component: <Text>{pv.varNm}</Text>,
          },
          uuid: detail.uuid,
          isDefault: detail.isDefault,
        };
      });

      if (mappedDetails) {
        let nodeIO: NodeInputOutput[] = [];

        if (selectedNode?.data.nodeTypCd === "SERVICE_TASK") {
          const mapped = resultOutputs.map((re) => {
            const md = mappedDetails.find(
              (me) => me.expression === re.expression
            );
            return md ? { ...md, name: re.name } : re;
          });

          nodeIO = [...mapped, ...mappedDetails.filter((mp) => !mp.isDefault)];
        } else if (
          selectedNode?.data.nodeTypCd === "SUB_PROC_TASK" &&
          subProcessRunAs === "SYNC"
        ) {
          console.log(
            "selectedSubProcessOutputsNames",
            selectedSubProcessOutputsNames
          );
          console.log("selectedSubProcessOutputs", selectedSubProcessOutputs);
          const mapped = selectedSubProcessOutputsNames.map((output) => {
            const expr = `exp!(return nv!result.${output})`;

            const md = mappedDetails.find((me) => me.expression === expr);
            return md
              ? { ...md, name: output }
              : ({
                  name: output,
                  uuid: v4(),
                  expression: expr,
                  ioType: "O",
                  target: undefined,
                  isDefault: true,
                } as NodeInputOutput);
          });

          nodeIO = [...mapped, ...mappedDetails.filter((mp) => !mp.isDefault)];
        } else {
          nodeIO = [...mappedDetails];
        }

        setNodeInputOutput(nodeIO);

        setSelectedInput(mappedDetails.find((io) => io.ioType === "I"));
        setSelectedOutput(
          mappedDetails.find((io) => io.ioType === "O" && !io.isDefault)
        );
      }
    }
  }, [
    nodes,
    processVariables,
    selectedSubProcessOutputsNames,
    subProcessRunAs,
  ]);

  const onCreate = async () => {
    handleNodeIoDetails(
      nodeId,
      {
        name: nodeName ? nodeName : String(selectedNode?.data.label),
        description: nodeDescription ? nodeDescription : "",
        displayName: nodeDisplayName
          ? nodeDisplayName
          : String(selectedNode?.data.label),
      },
      nodeInputOutput
        .filter((io) => io.target !== undefined)
        .map((it) => ({
          uuid: it.uuid,
          ioType: it.ioType,
          expression: it.expression,
          target: it.target?.id,
          isDefault: it.isDefault,
        })),
      ["XOR_GW", "OR_GW", "AND_GW"].includes(
        selectedNode?.data.nodeTypCd as string
      )
        ? gateWayDetails.map((gwd) => ({
            expression: gwd.expression,
            nextNodeUuid: gwd.result,
          }))
        : undefined,
      selectedNode?.data.nodeTypCd === "SERVICE_TASK"
        ? {
            integrationUuid: selectedHttpIntegration?.uuid as string,
            inputs: selectedHttpIntegrationInputs,
            targetVariableUuid: selectedHttpIntegrationTargetVariableUuid,
          }
        : undefined,
      selectedNode?.data.nodeTypCd === "SUB_PROC_TASK"
        ? {
            subProcess: selectedSubProcess?.uuid,
            expression: selectedSubProcessExpression,
            isDynamic: selectedSubProcessIsExpression,
            runAs: subProcessRunAs,
            subProcessInputs: selectedSubProcessInputExpression,
            subProcessVariables: selectedSubProcessVariables,
            subProcessOutputs: selectedSubProcessOutputs,
          }
        : undefined
    );
    onClose();
  };

  const defineGroovyLanguage = (
    monacoInstance: any,
    ruleMatchers: string[],
    pvMatchers: string[],
    processModelMatchers: string[]
  ) => {
    if (!monacoInstance) return;

    // Register Groovy language
    monacoInstance.languages.register({ id: "groovy" });

    // Define the syntax highlighting rules using Monarch tokens
    monacoInstance.languages.setMonarchTokensProvider("groovy", {
      tokenizer: {
        root: [
          [
            /\b(def|class|if|else|while|for|return|import|package|public|private|protected|static|void|true|false|null)\b/,
            "keyword",
          ],
          [/pv![a-zA-Z_$][\w$]*(?:\s*\([^\)]*\))?/, "constant"],
          [/(rule![a-zA-Z_][a-zA-Z0-9_]*|exp!)(?=\s*\([^)]*\))/, "comment"],
          [/[{}()\[\]]/, "@brackets"],
          [/"/, "string", "@string"],
          [/\b\d+(\.\d+)?\b/, "number"],
          [/\/\*[\s\S]*?\*\//, "comment"],
          [/\/\/.*$/, "comment"],
        ],
        string: [
          [/[^\\"]+/, "string"],
          [/\\./, "string.escape"],
          [/"/, "string", "@pop"],
        ],
      },
    });

    // Define custom theme for Groovy syntax highlighting
    monacoInstance.editor.defineTheme("custom-theme", {
      base: "vs-dark",
      inherit: true,
      rules: [
        { token: "keyword", foreground: "569CD6" },
        { token: "constant", foreground: "4EC9B0", fontStyle: "italic" },
        { token: "comment", foreground: "6A9955", fontStyle: "italic" },
        { token: "string", foreground: "CE9178" },
        { token: "number", foreground: "B5CEA8" },
      ],
      colors: {
        "editor.foreground": "#D4D4D4",
        "editor.background": "#1E1E1E",
      },
    });

    // Ensure unique suggestions for rule and pv matchers
    const uniqueRuleMatchers = Array.from(new Set(ruleMatchers));
    const uniquePvMatchers = Array.from(new Set(pvMatchers));
    const uniqueProcessModelMatchers = Array.from(
      new Set(processModelMatchers)
    );

    // Register completion provider for custom rule suggestion
    const provider = monacoInstance.languages.registerCompletionItemProvider(
      "groovy",
      {
        triggerCharacters: ["!", "(", " "],
        provideCompletionItems: (model: any, position: any) => {
          const textBeforeCursor = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: Math.max(1, position.column - 20),
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          // Suggest rule! unique matches
          if (textBeforeCursor.endsWith("rule!")) {
            return {
              suggestions: uniqueRuleMatchers.map((suggestion) => ({
                label: suggestion,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 5,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          // Suggest pv! unique matches
          if (textBeforeCursor.endsWith("pv!")) {
            return {
              suggestions: uniquePvMatchers.map((suggestion) => ({
                label: suggestion,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 3,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          // Suggest process model unique matches
          if (textBeforeCursor.endsWith("pm!")) {
            return {
              suggestions: uniqueProcessModelMatchers.map((suggestion) => ({
                label: suggestion,
                kind: monacoInstance.languages.CompletionItemKind.Text,
                insertText: suggestion,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column - 3,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          return { suggestions: [] };
        },
      }
    );

    // Dispose provider on unmount to avoid duplicate instances
    return () => {
      provider.dispose();
    };
  };

  useEffect(() => {
    let disposeProvider: any;

    if (monaco) {
      disposeProvider = defineGroovyLanguage(
        monaco,
        ruleMatchers,
        pvMatchers,
        processModelMatchers
      );
    }

    return () => {
      if (disposeProvider) {
        disposeProvider();
      }
    };
  }, [monaco, ruleMatchers, pvMatchers]);

  // Editor options, including the custom theme and language
  const editorOptions = {
    selectOnLineNumbers: true,
    minimap: { enabled: false },
    readOnly: false,
  };

  const onNewExpression = (ioType: "I" | "O") => {
    const newExpression = {
      name: "",
      uuid: v4(),
      expression: "",
      ioType,
      target: processVariables[0]
        ? {
            id: processVariables[0].uuid,
            name: processVariables[0].varNm,
            component: <Text>{processVariables[0].varNm}</Text>,
          }
        : undefined,
      isDefault: false,
    };
    setNodeInputOutput((prev) => [...prev, newExpression]);
    if (ioType === "I") setSelectedInput(newExpression);
    else setSelectedOutput(newExpression);
  };

  const onExpressionDelete = (uuid: string) => {
    setNodeInputOutput((prev) => prev.filter((it) => it.uuid !== uuid));
  };

  const onExpressionEdit = (
    e: string | undefined,
    selected: NodeInputOutput | undefined
  ) => {
    if (selected && e) {
      const nodeInputOutputCopy = [...nodeInputOutput];

      console.info(e, selected);

      if (!selected.isDefault) selected.name = e;
      selected.expression = e;

      nodeInputOutputCopy[
        nodeInputOutputCopy.findIndex((it) => it.uuid === selected.uuid)
      ] = selected;

      if (selected.ioType === "I") setSelectedInput(selected);
      else setSelectedOutput(selected);

      setNodeInputOutput(nodeInputOutputCopy);
    }
  };

  const onTargetSelect = (
    option: Option,
    selected: NodeInputOutput | undefined
  ) => {
    if (selected) {
      const nodeInputOutputCopy = [...nodeInputOutput];
      const index = nodeInputOutput.findIndex(
        (it) => it.uuid === selected.uuid
      );

      if (index !== -1) {
        selected.target = option;
        nodeInputOutputCopy[index] = selected;
        setNodeInputOutput(nodeInputOutputCopy);
      }

      if (selectedInput && selected.ioType === "I") {
        const selectedInputCopy: NodeInputOutput = {
          ...selectedInput,
          target: option,
        };
        selectedInputCopy.target = option;
        setSelectedInput(selectedInputCopy);
      } else if (selectedOutput) {
        const selectedOuputCopy: NodeInputOutput = {
          ...selectedOutput,
          target: option,
        };
        selectedOuputCopy.target = option;
        setSelectedOutput(selectedOuputCopy);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={initialRef}
      size="5xl"
      isCentered
    >
      <ModalOverlay
        zIndex={1}
        backdropFilter="blur(10px)" // ← This will blur the page behind the modal
        bg="rgba(0, 0, 0, 0.4)"
      />
      <ModalContent
        maxH={"85vh"}
        overflow={"auto"}
        bg="rgba(0, 0, 0, 0.9)"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255, 255, 255, 0.4)"
        borderRadius="12px"
        color="white"
      >
        <ModalHeader>Node Configuration Properties</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs isManual variant="enclosed">
            <TabList>
              <Tab
                color="white"
                _selected={{
                  color: "white",
                  background: "linear-gradient(to right, #7652f7, #9500ff)",
                  borderColor: "transparent",
                  borderRadius: "md",
                }}
              >
                General
              </Tab>
              <Tab
                color="white"
                _selected={{
                  color: "white",
                  background: "linear-gradient(to right, #7652f7, #9500ff)",
                  borderColor: "transparent",
                  borderRadius: "md",
                }}
              >
                Data
              </Tab>
              {/* <Tab>Setup</Tab> */}
              {selectedNode?.data.nodeTypCd === "SERVICE_TASK" && (
                <Tab
                  color="white"
                  _selected={{
                    color: "white",
                    background: "linear-gradient(to right, #7652f7, #9500ff)",
                    borderColor: "transparent",
                    borderRadius: "md",
                  }}
                >
                  Service
                </Tab>
              )}
              {["XOR_GW", "AND_GW", "OR_GW"].includes(
                selectedNode?.data.nodeTypCd as string
              ) && (
                <Tab
                  color="white"
                  _selected={{
                    color: "white",
                    background: "linear-gradient(to right, #7652f7, #9500ff)",
                    borderColor: "transparent",
                    borderRadius: "md",
                  }}
                >
                  Decision
                </Tab>
              )}
              {selectedNode?.data.nodeTypCd === "SUB_PROC_TASK" && (
                <Tab
                  color="white"
                  _selected={{
                    color: "white",
                    background: "linear-gradient(to right, #7652f7, #9500ff)",
                    borderColor: "transparent",
                    borderRadius: "md",
                  }}
                >
                  Setup
                </Tab>
              )}
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <Tabs size="md" variant="enclosed" gap={2} mx={0} my={2}>
                  <TabList>
                    <Tab
                      color="white"
                      _selected={{
                        color: "white",
                        background:
                          "linear-gradient(to right, #7652f7, #9500ff)",
                        borderColor: "transparent",
                        borderRadius: "md",
                      }}
                    >
                      English(us)
                    </Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel gap={2}>
                      <HStack>
                        <Text
                          fontWeight={"semibold"}
                          minWidth={"30%"}
                          textAlign="right"
                        >
                          Name
                        </Text>

                        <Input
                          placeholder={"name"}
                          width={"70%"}
                          value={nodeName}
                          my={2}
                          onChange={(e) => setNodeName(e.target.value)}
                          bg="rgba(255, 255, 255, 0.1)"
                          border="1px solid rgba(255, 255, 255, 0.3)"
                          color="white"
                          _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                          _focus={{
                            borderColor: "brand.500",
                            boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.6)",
                          }}
                        />
                      </HStack>

                      <HStack>
                        <Text
                          fontWeight={"semibold"}
                          minWidth={"30%"}
                          justifyContent={"flex-end"}
                          textAlign="right"
                        >
                          Description
                        </Text>

                        <Textarea
                          placeholder={"Description"}
                          value={nodeDescription}
                          justifyContent={"start"}
                          my={2}
                          width={"70%"}
                          onChange={(e) => setNodeDescription(e.target.value)}
                          bg="rgba(255, 255, 255, 0.1)"
                          border="1px solid rgba(255, 255, 255, 0.3)"
                          color="white"
                          _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                          _focus={{
                            borderColor: "brand.500",
                            boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.6)",
                          }}
                        />
                      </HStack>
                      <HStack>
                        <Text
                          fontWeight={"semibold"}
                          minWidth={"30%"}
                          textAlign="right"
                        >
                          Task Display name
                        </Text>

                        <Input
                          placeholder={"display name"}
                          value={nodeDisplayName}
                          width={"70%"}
                          justifyContent={"start"}
                          my={2}
                          onChange={(e) => setNodeDisplayName(e.target.value)}
                          bg="rgba(255, 255, 255, 0.1)"
                          border="1px solid rgba(255, 255, 255, 0.3)"
                          color="white"
                          _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                          _focus={{
                            borderColor: "brand.500",
                            boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.6)",
                          }}
                        />
                        <Icon
                          as={BiEdit as React.ElementType}
                          boxSize={"24px"}
                          color={"brand.500"}
                          onClick={onExpressionEditorModalOpen}
                        />
                      </HStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>{" "}
              </TabPanel>
              <TabPanel>
                <DataProperties
                  processVariableOptions={processVariableOptions}
                  nodeInputOutput={nodeInputOutput}
                  selectedInput={selectedInput}
                  selectedOutput={selectedOutput}
                  setSelectedInput={setSelectedInput}
                  setSelectedOutput={setSelectedOutput}
                  onNewExpression={onNewExpression}
                  onExpressionDelete={onExpressionDelete}
                  onExpressionEdit={onExpressionEdit}
                  onTargetSelect={onTargetSelect}
                  defineGroovyLanguage={defineGroovyLanguage}
                  processModelMatchers={processModelMatchers}
                  ruleMatchers={ruleMatchers}
                  pvMatchers={pvMatchers}
                  reverseExpressionHandler={reverseExpressionHandler}
                />
              </TabPanel>
              {selectedNode?.data.nodeTypCd === "SERVICE_TASK" && (
                <TabPanel>
                  <ServiceProperties
                    httpIntegrations={httpIntegrations}
                    selectedHttpIntegration={selectedHttpIntegration}
                    setSelectedHttpIntegration={setSelectedHttpIntegration}
                    selectedHttpIntegrationInputs={
                      selectedHttpIntegrationInputs
                    }
                    setSelectedHttpIntegrationInputs={
                      setSelectedHttpIntegrationInputs
                    }
                    selectedHttpIntegrationTargetVariableUuid={
                      selectedHttpIntegrationTargetVariableUuid
                    }
                    setSelectedHttpIntegrationTargetVariableUuid={
                      setSelectedHttpIntegrationTargetVariableUuid
                    }
                    defineGroovyLanguage={defineGroovyLanguage}
                    processModelMatchers={processModelMatchers}
                    ruleMatchers={ruleMatchers}
                    pvMatchers={pvMatchers}
                    dataTypes={dataTypes}
                  />
                </TabPanel>
              )}
              {["XOR_GW", "AND_GW", "OR_GW"].includes(
                selectedNode?.data.nodeTypCd as string
              ) && (
                <TabPanel>
                  <GatewayProperties
                    selectedNode={selectedNode}
                    gateWayDetails={gateWayDetails}
                    setGatewayDetails={setGatewayDetails}
                  />
                </TabPanel>
              )}

              {selectedNode?.data.nodeTypCd === "SUB_PROC_TASK" && (
                <TabPanel>
                  <SubProcessProperties
                    selectedNode={selectedNode}
                    selectedSubProcess={selectedSubProcess}
                    setSelectedSubProcess={setSelectedSubProcess}
                    selectedSubProcessVariables={selectedSubProcessVariables}
                    setSelectedSubProcessVariables={
                      setSelectedSubProcessVariables
                    }
                    selectedSubProcessOutputs={selectedSubProcessOutputs}
                    setSelectedSubProcessOutputs={setSelectedSubProcessOutputs}
                    selectedSubProcessOutputsNames={
                      selectedSubProcessOutputsNames
                    }
                    setSelectedSubProcessOutputsNames={
                      setSelectedSubProcessOutputsNames
                    }
                    selectedSubProcessExpression={selectedSubProcessExpression}
                    setSelectedSubProcessExpression={
                      setSelectedSubProcessExpression
                    }
                    selectedSubProcessInputExpression={
                      selectedSubProcessInputExpression
                    }
                    setSelectedSubProcessInputExpression={
                      setSelectedSubProcessInputExpression
                    }
                    selectedSubProcessIsExpression={
                      selectedSubProcessIsExpression
                    }
                    setSelectedSubProcessIsExpression={
                      setSelectedSubProcessIsExpression
                    }
                    subProcessRunAs={subProcessRunAs}
                    setSubProcessRunAs={setSubProcessRunAs}
                    defineGroovyLanguage={defineGroovyLanguage}
                    processModelMatchers={processModelMatchers}
                    ruleMatchers={ruleMatchers}
                    pvMatchers={pvMatchers}
                    dataTypes={dataTypes}
                  />
                </TabPanel>
              )}

              {/*setup*/}
              <TabPanel>
                <EmailProperties />
              </TabPanel>
            </TabPanels>
          </Tabs>
          <HStack justifyContent={"end"} mt={6} mb={4} px={3}>
            <BrandButton onClick={onCreate}>Save</BrandButton>
          </HStack>

          <ExpressionEditorModal
            isOpen={isExpressionEditorModalOpen}
            onClose={onExpressionEditorModalClose}
            value={nodeDisplayName}
            handleValue={handleValue}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
